import { CREATE_QUOTE_PAGE } from "../constants";
import { getApiUrl } from "../utils/get-api-url";

export interface GetPropertyDetailsProps {
  propertyId: string;
  productId: string;
}

export interface AddressData {
  propertyStreetNumber: string;
  propertyStreetName: string;
  propertyAddressLine2?: string;
  propertyCity: string;
  propertyState: string;
  propertyZipCode?: string;
  latitude?: string;
  longitude?: string;
}

export interface PostPropertyDetailsProps extends AddressData {
  productId: string;
  geocodeMatchLevel?: string;
  quoteOriginationSystem: string;
}

export interface PutPropertyDetailsProps {
  propertyId: string;
  productId: string;
  geocodeMatchLevel?: string;
  quoteOriginationSystem: string;
}

export interface PropertyDetailsResponse {
  done: boolean;
  result: ResultSuccess | ResultError;
}

export interface ResultError {
  error: Error;
}

export interface Error {
  code: string;
  message: string;
}

export interface ResultSuccess {
  response: Response;
}

export interface Response {
  property: Property;
}

export interface Property {
  id: string;
  address: Address;
  details: Details;
}

export interface Details {
  RespondingFireDistrictRetrieved: string;
  PropertyHazardLocationELEV: string;
  DistanceToCoastRMS: string;
  PropertyHazardLocationSURE: string;
  Latitude: string;
  LongitudeRMS: string;
  AddressValidationVendorPDFLink: string;
  DistanceToFireDepartment: string;
  LongitudeRetrieved: string;
  DistanceToCoastSecondary: string;
  GeocodeMatchLevel: string;
  WPI8CertificateType: string;
  Elevation: string;
  BCEquivalentStandard: string;
  WindPoolTierRetrieved: string;
  DistanceToPrimaryBrushFireZone: string;
  SquareFootUnderRoofRetrieved: string;
  ElevationRMS: string;
  BCEquivalentStandardRetrieved: string;
  AddressChanged: string;
  ConstructionYear: string;
  GeoMatchLevelRMS: string;
  NumberOfStories: string;
  BarrierIsland: string;
  NumberOfStoriesRetrieved: string;
  FloodZoneCodeRetrieved: string;
  PropertyHazardLocationIMFIC: string;
  LatitudeRMS: string;
  Longitude: string;
  FireStationRetrieved: string;
  GeocodeMatchLevelVendor: string;
  PropertyCounty: string;
  PropertyWildFireModelVersion: string;
  PropertyHazardLocation: string;
  FloodZone: string;
  NumberOfHalfBathsRetrieved: string;
  BCEquivalentLocationRetrieved: string;
  PropertyHazardLocationPIC: string;
  SquareFootUnderRoofFLA: string;
  NumberOfFullBathsRetrieved: string;
  PropertyCountyRetrieved: string;
  DistanceToFireDepartmentRetrieved: string;
  PropertyHazardLocationFNIC: string;
  AddressValidated: string;
  WindPoolTier: string;
  AddressValidationVendorPDFExpirationDate: string;
  WPI8CertificateBuildingCode: string;
  ConstructionYearRetrieved: string;
  FloodZoneFEMA: string;
  NumberOfHalfBaths: string;
  ProtectionClassSplitA: string;
  RFDIDRetrieved: string;
  ProtectionClassSplitB: string;
  BCEquivalentRetrieved: string;
  PropertyHazardLocationWIC: string;
  DistanceWildlandFeet: string;
  FloodZoneRetrieved: string;
  PropertyWildFireRiskScore: string;
  PropertyHazardLocationOFCC: string;
  BCEquivalent: string;
  BCEquivalentLocation: string;
  NumberOfFullBaths: string;
  ProtectionClass: string;
  FloodZoneFEMARetrieved: string;
  LatitudeRetrieved: string;
  DistanceToCoast: string;
}

export interface Address {
  propertyStreetNumber: string;
  propertyStreetName: string;
  propertyAddressLine2: string;
  propertyCity: string;
  propertyState: string;
  propertyZipCode: string;
  propertyZipCodePlusFour: string;
  latitude: string;
  longitude: string;
}

const handleError = async (response, message = "Address servicing error.") => {
  const errorResult = await response.json();
  if (errorResult?.result?.error?.code && errorResult?.result?.error?.message) {
    throw new Error(
      `${errorResult.result.error.code} - ${errorResult.result.error.message}`
    );
  } else {
    throw new Error(message);
  }
};

const apiUrl = getApiUrl("addressservicing");

export const getPropertyDetails = async ({
  propertyId,
  productId
}: GetPropertyDetailsProps): Promise<Property> | never => {
  const fullUrl = `${apiUrl}/properties/${propertyId}/${productId}`;

  const response = await fetch(fullUrl, {
    method: "GET",
    credentials: "include"
  });

  if (!response.ok) {
    await handleError(response);
  }

  const { result } = await response.json();
  return result.response.property;
};

export const postPropertyDetails = async ({
  productId,
  geocodeMatchLevel = "ROOF",
  quoteOriginationSystem = CREATE_QUOTE_PAGE.QUOTE_ORIGINATION_SYSTEM.AP,
  ...addressData
}: PostPropertyDetailsProps): Promise<Property> | never => {
  const fullUrl = `${apiUrl}/properties?async=false`;

  const response = await fetch(fullUrl, {
    method: "POST",
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
      "Cache-Control": "no-cache"
    },
    body: JSON.stringify({
      productId,
      ...addressData,
      geocodeMatchLevel,
      quoteOriginationSystem
    }),
    credentials: "include"
  });

  if (!response.ok) {
    await handleError(
      response,
      "Unable to post this address to address servicing."
    );
  }

  const { result } = await response.json();
  return result.response.property;
};

export const putPropertyDetails = async ({
  propertyId,
  productId,
  geocodeMatchLevel = "ROOF",
  quoteOriginationSystem = CREATE_QUOTE_PAGE.QUOTE_ORIGINATION_SYSTEM.AP,
  ...addressData
}: PutPropertyDetailsProps): Promise<Property> | never => {
  const fullUrl = `${apiUrl}/properties/${propertyId}/${productId}`;

  const response = await fetch(fullUrl, {
    method: "PUT",
    body: JSON.stringify({
      productId,
      ...addressData,
      geocodeMatchLevel,
      quoteOriginationSystem
    }),
    credentials: "include"
  });

  if (!response.ok) {
    await handleError(
      response,
      "Unable to update property in address servicing"
    );
  }

  const { result } = await response.json();
  return result.response.property;
};
